/**
 * Live validation without a validator in the browser.
 *
 * It debounces, cancels the in-flight request on a newer edit, and holds the last good result
 * while a new one is pending so the block does not flicker empty. What it never does is decide
 * anything: the sentences are the server's, verbatim (§6.3).
 */

import { useEffect, useRef, useState } from 'react';

export interface ValidationState {
  errors: readonly string[];
  warnings: readonly string[];
  wouldWrite: boolean;
  pending: boolean;
  /** A failure of the check itself, which is not a failure of the draft. */
  unavailable: string | null;
}

export interface ValidationResult {
  errors: string[];
  warnings: string[];
  wouldWrite: boolean;
}

export interface DebouncedValidationOptions<B> {
  body: B;
  /** False while the form has nothing worth asking about. */
  enabled?: boolean;
  delayMs?: number;
  validate: (body: B, signal: AbortSignal) => Promise<ValidationResult>;
}

/** The last answer, and the draft it was an answer to. */
interface Answered {
  forDraft: string | null;
  errors: readonly string[];
  warnings: readonly string[];
  wouldWrite: boolean;
  unavailable: string | null;
}

const NOTHING_ASKED: Answered = {
  forDraft: null,
  errors: [],
  warnings: [],
  wouldWrite: false,
  unavailable: null,
};

export function useDebouncedValidation<B>({
  body,
  enabled = true,
  delayMs = 250,
  validate,
}: DebouncedValidationOptions<B>): ValidationState {
  const [answered, setAnswered] = useState<Answered>(NOTHING_ASKED);

  // The caller passes a fresh closure every render; the effect must not restart on that alone.
  const validateRef = useRef(validate);
  useEffect(() => {
    validateRef.current = validate;
  });

  const serialised = JSON.stringify(body);

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    const timer = setTimeout(() => {
      validateRef
        .current(JSON.parse(serialised) as B, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return;
          setAnswered({
            forDraft: serialised,
            errors: result.errors,
            warnings: result.warnings,
            wouldWrite: result.wouldWrite,
            unavailable: null,
          });
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          setAnswered((previous) => ({
            ...previous,
            forDraft: serialised,
            unavailable:
              error instanceof Error
                ? error.message
                : 'The engine could not check this draft just now.',
          }));
        });
    }, delayMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [serialised, enabled, delayMs]);

  if (!enabled) {
    return { errors: [], warnings: [], wouldWrite: false, pending: false, unavailable: null };
  }

  return {
    errors: answered.errors,
    warnings: answered.warnings,
    wouldWrite: answered.wouldWrite,
    unavailable: answered.unavailable,
    // A newer edit than the last answer means a check is in flight; the last good result stays
    // visible underneath rather than the block flickering empty.
    pending: answered.forDraft !== serialised,
  };
}
